import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { createAPIClient } from '../../api';
import * as types from '../../api/types';
import { PrintLayout, usePrintData } from '../../components/PrintLayout';
import { discountRuleLabel, invoiceDate, invoiceMoney, isFinanceChargeInvoice } from '../invoiceUi';
import { isZeroAmount } from '../reports/reportUi';
import { customerAddressLines, documentMarkFor } from './printUi';
import { DocumentMark } from './PrintParts';

interface InvoicePrintData {
  invoice: types.Invoice;
  customer: types.Customer;
  company: types.CompanySettings;
}

const cell = 'px-2 py-2 align-top';
const numericCell = `${cell} text-right whitespace-nowrap`;

/** /print/invoices/:id — the invoice as the customer receives it. */
export function InvoicePrintPage() {
  const { id = '' } = useParams<{ id: string }>();
  const api = useMemo(() => createAPIClient(), []);
  const state = usePrintData<InvoicePrintData>(id, async () => {
    const [invoice, company] = await Promise.all([api.getInvoice(id), api.getCompanySettings()]);
    const customer = await api.getCustomer(invoice.billingCustomerId);
    return { invoice, customer, company };
  }, 'Could not load invoice');
  const data = state.data;
  const invoice = data?.invoice;
  const title = invoice ? `Invoice ${invoice.invoiceNumber}${data?.customer.name ? ` - ${data.customer.name}` : ''}` : 'Invoice';

  return (
    <PrintLayout title={title} backTo={`/invoices/${id}`} backLabel="Back to invoice" noun="invoice" state={state} company={data?.company ?? null}>
      {data && invoice && <InvoiceDocument invoice={invoice} customer={data.customer} />}
    </PrintLayout>
  );
}

function InvoiceDocument({ invoice, customer }: { invoice: types.Invoice; customer: types.Customer }) {
  // Server amounts only, as on InvoiceDetailPage: older responses without
  // subtotal/discounts simply have no discounts.
  const discounts = invoice.discounts ?? [];
  const hasDiscounts = discounts.length > 0;
  const mark = documentMarkFor(invoice.status);
  const addressLines = customerAddressLines(customer);
  const headings = ['Description', 'Qty', 'Unit price', 'Amount', ...(hasDiscounts ? ['Discount'] : []), 'Tax'];

  return (
    <>
      <section className="print-keep mb-8 flex flex-wrap items-start justify-between gap-6">
        <div>
          <h1 className="text-2xl font-bold tracking-wide">{isFinanceChargeInvoice(invoice) ? 'FINANCE CHARGE INVOICE' : 'INVOICE'}</h1>
          <p className="mt-1 text-base font-semibold">#{invoice.invoiceNumber}</p>
          {mark && <DocumentMark mark={mark} />}
        </div>
        <dl className="grid grid-cols-[auto_auto] gap-x-4 gap-y-1">
          <dt className="text-gray-600">Invoice date</dt><dd>{invoiceDate(invoice.invoiceDate)}</dd>
          <dt className="text-gray-600">Due date</dt><dd>{invoiceDate(invoice.dueDate)}</dd>
          {invoice.periodStartDate && <><dt className="text-gray-600">Service period</dt><dd>{invoiceDate(invoice.periodStartDate)} to {invoiceDate(invoice.periodEndDate)}</dd></>}
        </dl>
      </section>

      <section className="print-keep mb-8">
        <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-600">Bill to</h2>
        <p className="font-semibold">{customer.name || invoice.billingCustomer?.name}</p>
        {addressLines.map((line) => <p key={line}>{line}</p>)}
        {(customer.email || invoice.billingCustomer?.email) && <p>{customer.email || invoice.billingCustomer?.email}</p>}
      </section>

      {isFinanceChargeInvoice(invoice) && <p className="mb-6">Late fee assessed on the overdue balance.</p>}

      <div className="overflow-x-auto print:overflow-visible">
        <table className="print-table w-full border-collapse text-sm" aria-label="Invoice line items">
          <thead>
            <tr className="border-b-2 border-gray-800">
              {headings.map((heading, index) => <th key={heading} className={`${index === 0 ? 'text-left' : 'text-right'} px-2 py-2 font-semibold`}>{heading}</th>)}
            </tr>
          </thead>
          <tbody>
            {invoice.lineItems.length === 0
              ? <tr><td colSpan={headings.length} className={`${cell} text-gray-600`}>No line items.</td></tr>
              : invoice.lineItems.map((line) => (
                <tr key={line.id} className="border-b border-gray-200">
                  <td className={cell}>{line.description || line.item?.name}</td>
                  <td className={numericCell}>{line.quantity}</td>
                  <td className={numericCell}>{invoiceMoney(line.unitPrice)}</td>
                  <td className={numericCell}>{invoiceMoney(line.lineAmount)}</td>
                  {hasDiscounts && <td className={numericCell}>{isZeroAmount(line.lineDiscount) ? '—' : `−${invoiceMoney(line.lineDiscount ?? '0')}`}</td>}
                  <td className={numericCell}>{invoiceMoney(line.lineTax)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <dl className="print-keep mt-6 ml-auto max-w-xs space-y-1" aria-label="Invoice totals">
        {hasDiscounts && <>
          <div className="flex justify-between gap-6"><dt>Subtotal</dt><dd>{invoiceMoney(invoice.subtotal ?? invoice.totalAmount)}</dd></div>
          {discounts.map((discount) => <div key={discount.id} className="flex justify-between gap-6"><dt>{discount.name} ({discountRuleLabel(discount)})</dt><dd>−{invoiceMoney(discount.amount)}</dd></div>)}
        </>}
        <div className="flex justify-between gap-6"><dt>Pre-tax</dt><dd>{invoiceMoney(invoice.totalAmount)}</dd></div>
        <div className="flex justify-between gap-6"><dt>Tax</dt><dd>{invoiceMoney(invoice.totalTax)}</dd></div>
        <div className="flex justify-between gap-6 border-t-2 border-gray-800 pt-2 text-base font-bold"><dt>Total</dt><dd>{invoiceMoney(invoice.total)}</dd></div>
      </dl>

      {invoice.portalCode && (
        <section className="print-keep mt-8 border-t border-gray-300 pt-4 text-sm" aria-label="Customer portal" data-testid="print-portal-code">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-600">Your online account</h2>
          <p className="mt-1">
            See your invoices and pay online. Open <strong>{`${window.location.origin}/portal/register`}</strong> and enter this code with your email address:
          </p>
          <p className="mt-1 font-mono text-lg font-bold tracking-widest">{invoice.portalCode}</p>
        </section>
      )}
    </>
  );
}
